import React, { useMemo, useCallback, useState, useEffect } from 'react';
import Api from 'api';
import cn from 'classnames';
import Modal from 'components/UI/Modal';
import CloseButton from 'components/UI/CloseButton';
import Loader from 'components/UI/Loader';
import Title from 'components/UI/Title';
import Slider from 'components/UI/Slider';
import { CustomDot, CustomPagination } from 'components/UI/Slider/utils';
import List from 'components/UI/List';
import Image from 'components/UI/Image';
import Markup from 'components/Markup';
import MediaPlayer from 'components/MediaPlayer';
import { NEWS_MODAL_STORAGE_KEY } from 'define/news';

import * as styles from './NewsModal.module.scss';

export default function NewsModal({ id, date, title, isOpen, handlePushEvent, ...props }) {
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [current, setCurrent] = useState(0);

  const setCurrentKey = useCallback((prev, next) => {
    setCurrent(next);
  }, []);

  const pushEvent = useCallback(() => {
    handlePushEvent('slide_change', title);
  }, []);

  useEffect(() => {
    const fetchData = () => {
      if (!isOpen) {
        return;
      }

      const cachedNews = JSON.parse(sessionStorage.getItem(NEWS_MODAL_STORAGE_KEY)) ?? {};

      if (cachedNews[id]) {
        setData(cachedNews[id]);
        setIsLoading(false);
      } else {
        Api.getNewsById(id)
          .then(res => {
            const updatedCache = { ...cachedNews, [id]: res };

            setData(res);
            sessionStorage.setItem(NEWS_MODAL_STORAGE_KEY, JSON.stringify(updatedCache));
          })
          .finally(() => setIsLoading(false));
      }
    };

    fetchData();
  }, [isOpen, id]);

  const sliderSettings = useMemo(
    () => ({
      customPaging: CustomDot.bind(null, data.visuals?.length, current),
      appendDots: CustomPagination,
      dotsClass: 'slick-pagination',
      dots: true,
      slidesToShow: 1,
      slidesToScroll: 1,
      initialSlide: 0,
      arrows: true,
      infinite: true,
      responsive: [
        {
          breakpoint: 1025,
          settings: {
            arrows: false,
          },
        },
      ],
    }),
    [data.visuals?.length, current],
  );

  return (
    <Modal
      closeButton={false}
      containerMod="newsModal"
      contentMod="newsModal"
      isOpen={isOpen}
      {...props}
    >
      {onClose => (
        <div className={cn(styles.layout, isLoading && styles.isLoading)} dir="ltr">
          <CloseButton className={styles.closeButton} onClose={onClose} />
          <p className={styles.date}>{date}</p>
          <Title tag="h2" size="h4" className={styles.title}>
            {title}
          </Title>
          {isLoading ? (
            <div className={styles.loaderContainer}>
              <Loader theme="fullHeight" />
            </div>
          ) : (
            <div className={cn('defaultSlider', styles.scrollWrapper)}>
              <Slider
                {...sliderSettings}
                beforeChange={setCurrentKey}
                afterChange={pushEvent}
                className={styles.slider}
              >
                {data?.visuals?.map(({ type, image, alt, video, videoPoster }, i) => {
                  switch (type) {
                    case 'video':
                      return (
                        <MediaPlayer
                          key={`visuals-item-${i}`}
                          className={styles.picture}
                          videoPoster={videoPoster}
                          videoSrc={video}
                          preload="metadata"
                        />
                      );
                    case 'image':
                    default:
                      return (
                        <Image
                          key={`visuals-item-${i}`}
                          src={image}
                          alt={alt}
                          className={styles.picture}
                          classImage={styles.img}
                        />
                      );
                  }
                })}
              </Slider>
              {data?.textContent?.map((el, i) => {
                switch (el.type) {
                  case 'list':
                    return (
                      <List
                        key={i}
                        className={styles.paragraph}
                        items={el.content}
                        theme="smallMarginChecked"
                        direction="ltr"
                      />
                    );
                  case 'text':
                  default:
                    return (
                      <Markup tag="p" key={i} className={styles.paragraph}>
                        {el.content}
                      </Markup>
                    );
                }
              })}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
